import { useEffect, useId } from "react";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useFocusTrap, useOverlayDismiss } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  dismissOnBackdropClick?: boolean;
  /**
   * Where focus should land when the dialog opens. Without it the trap takes
   * the first focusable descendant, which is the header's Close button — safe,
   * but on a form dialog the first field is the better answer. Point this at
   * that field.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Opt OUT of the dialog's own focus management, for a caller that already
   * runs its own `useFocusTrap` over its body.
   *
   * <p>Two traps nested inside one another do not compose: both attach a Tab
   * handler, the inner one calls `preventDefault` without `stopPropagation`, so
   * the event bubbles to the outer handler which re-reads a
   * `document.activeElement` the inner one has already moved — and focus jumps
   * twice at the cycle boundary. Both would also capture and restore, leaving
   * restore order ambiguous.</p>
   *
   * <p>Reach for this only when the caller's trap does something this one
   * cannot — the live case is a wizard re-keying initial focus per step. A
   * caller that just wants a different starting element should pass
   * `initialFocusRef` instead.</p>
   */
  manageFocus?: boolean;
}

const sizeClass: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  dismissOnBackdropClick = true,
  initialFocusRef,
  manageFocus = true,
}: ModalProps) {
  // Unique per instance. This was the literal string "modal-title", so two open
  // dialogs put the same id on two <h2>s and `aria-labelledby` resolved to
  // whichever the document hit first — a stacked dialog could announce the name
  // of the one underneath it.
  const titleId = useId();

  // Focus management, scoped to the dialog CARD so the header's Close button is
  // inside the Tab cycle — a caller therefore does not have to supply its own
  // cancel control to keep the dialog escapable by keyboard.
  const trap = useFocusTrap<HTMLDivElement>({ active: open && manageFocus });

  // Escape and the scroll lock are stack-aware. Each open overlay used to carry
  // its own document listener, so one Escape ran all of them: a confirm dialog
  // raised from inside a drawer dismissed the drawer underneath it too. The
  // inner overlay's cleanup also unlocked page scroll while the outer one was
  // still modal.
  useOverlayDismiss(open, onClose);

  // Runs AFTER the trap's own initial-focus effect (hook order), so an explicit
  // target wins over "first focusable descendant" without the trap needing to
  // know about it.
  useEffect(() => {
    if (!open || !manageFocus) return;
    const target = initialFocusRef?.current;
    if (target && target.isConnected) target.focus();
  }, [open, manageFocus, initialFocusRef]);

  if (!open) return null;

  // Portaled to <body>: an in-place fixed overlay inherits its ancestors'
  // paint/hit-test context, so a modal mounted inside hidden content (e.g.
  // a closed <details>) would scroll-lock the page while rendering nothing
  // — the Defaults & Workout freeze. The portal makes visibility depend
  // only on `open`.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-space-4 bg-onyx/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={dismissOnBackdropClick ? onClose : undefined}
    >
      <div
        ref={manageFocus ? trap.containerRef : undefined}
        onKeyDown={manageFocus ? trap.onKeyDown : undefined}
        className={cn(
          "bg-white-pure rounded-lg shadow-lg w-full border-t-[2px] border-t-gold",
          "flex flex-col max-h-[90vh]",
          sizeClass[size]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-space-3 p-space-4 border-b border-border-light">
          <div className="flex-1 min-w-0">
            <h2
              id={titleId}
              className="font-display font-bold text-h3 text-onyx normal-case tracking-normal break-words"
            >
              {title}
            </h2>
            {description && (
              <p className="text-small text-gray-mid mt-space-1">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-space-1 rounded-full hover:bg-white-warm"
            aria-label="Close"
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-space-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-space-3 p-space-4 border-t border-border-light bg-white-warm">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
